import { useLocation, useMatches } from '@remix-run/react';
import type {
  CountryCode,
  CurrencyCode,
  Menu,
  MenuItem,
} from '@shopify/hydrogen/storefront-api-types';
import { parse as parseCookie } from 'worktop/cookie';

import { countries } from '~/common/constants/countries';
import { COOKIE_NAMES } from '~/common/constants/localStorage';
import {
  REGION_DEFAULT_COUNTRY_CODES,
  REGION_FALLBACK_CURRENCY,
  type Region,
} from '~/common/constants/region';
import { isValidRegion, resolveRegionInfo } from '~/common/utils/region';

import { ProductPrices } from '~/types/product';
import type { I18nLocale, Locale } from './type';

export interface EnhancedMenuItem extends MenuItem {
  to: string;
  target: string;
  isExternal?: boolean;
  items: EnhancedMenuItem[];
}

export interface EnhancedMenu extends Menu {
  items: EnhancedMenuItem[];
}

export function missingClass(string?: string, prefix?: string) {
  if (!string) {
    return true;
  }

  const regex = new RegExp(` ?${prefix}`, 'g');
  return string.match(regex) === null;
}

export function getExcerpt(text: string) {
  const regex = /<p.*>(.*?)<\/p>/;
  const match = regex.exec(text);
  return match?.length ? match[0] : text;
}

export function isNewArrival(date: string, daysOld = 30) {
  return (
    new Date(date).valueOf() >
    new Date().setDate(new Date().getDate() - daysOld).valueOf()
  );
}

export function isDiscounted(prices: ProductPrices) {
  if (!prices.compareAtPriceRange) {
    return false;
  }

  const compareAtPriceRangeMaxPrice = parseFloat(
    prices.compareAtPriceRange?.maxPrice,
  );

  const maxPrice = parseFloat(prices?.maxPrice);

  if (compareAtPriceRangeMaxPrice > maxPrice) {
    return true;
  }

  return false;
}

// Cart lines come in two shapes depending on whether they originate from the
// Shopify cart payload or from a locally-built line item.
export interface CartLineLike {
  merchandise?: { id?: string };
  merchandiseId?: string;
}

/** True when any cart line references one of `variantIds`. */
export function cartContainsVariant(
  lines: CartLineLike[] | null | undefined,
  variantIds: string[],
): boolean {
  if (!lines || lines.length === 0) return false;
  if (variantIds.length === 0) return false;

  const variantIdSet = new Set(variantIds);

  return lines.some((line) => {
    const id = line.merchandise?.id ?? line.merchandiseId;

    return id ? variantIdSet.has(id) : false;
  });
}

function resolveToFromType(
  {
    customPrefixes,
    pathname,
    type,
  }: {
    customPrefixes: Record<string, string>;
    pathname?: string;
    type?: string;
  } = {
    customPrefixes: {},
  },
) {
  if (!pathname || !type) return '';

  /*
    MenuItemType enum
    @see: https://shopify.dev/api/storefront/unstable/enums/MenuItemType
  */
  const defaultPrefixes = {
    BLOG: 'blogs',
    COLLECTION: 'collections',
    COLLECTIONS: 'collections', // Collections All (not documented)
    FRONTPAGE: 'frontpage',
    HTTP: '',
    PAGE: 'pages',
    CATALOG: 'collections/all', // Products All
    PRODUCT: 'products',
    SEARCH: 'search',
    SHOP_POLICY: 'policies',
  };

  const pathParts = pathname.split('/');
  const handle = pathParts.pop() || '';
  const routePrefix: Record<string, string> = {
    ...defaultPrefixes,
    ...customPrefixes,
  };

  switch (true) {
    // special cases
    case type === 'FRONTPAGE':
      return '/';

    case type === 'ARTICLE': {
      const blogHandle = pathParts.pop();
      return routePrefix.BLOG
        ? `/${routePrefix.BLOG}/${blogHandle}/${handle}/`
        : `/${blogHandle}/${handle}/`;
    }

    case type === 'COLLECTIONS':
      return `/${routePrefix.COLLECTIONS}`;

    case type === 'SEARCH':
      return `/${routePrefix.SEARCH}`;

    case type === 'CATALOG':
      return `/${routePrefix.CATALOG}`;

    // common cases: BLOG, PAGE, COLLECTION, PRODUCT, SHOP_POLICY, HTTP
    default:
      return routePrefix[type]
        ? `/${routePrefix[type]}/${handle}`
        : `/${handle}`;
  }
}

/*
  Parse each menu link and adding, isExternal, to and target
*/
function parseItem(customPrefixes = {}) {
  return function (item: MenuItem): EnhancedMenuItem {
    if (!item?.url || !item?.type) {
      // eslint-disable-next-line no-console
      console.warn('Invalid menu item.  Must include a url and type.');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return;
    }

    // extract path from url because we don't need the origin on internal to attributes
    const { pathname } = new URL(item.url);

    /*
      Currently the MenuAPI only returns online store urls e.g — xyz.myshopify.com/..
      Note: update logic when API is updated to include the active qualified domain
    */
    const isInternalLink = /\.myshopify\.com/g.test(item.url);

    const parsedItem = isInternalLink
      ? // internal links
        {
          ...item,
          isExternal: false,
          target: '_self',
          to: resolveToFromType({ type: item.type, customPrefixes, pathname }),
        }
      : // external links
        {
          ...item,
          isExternal: true,
          target: '_blank',
          to: item.url,
        };

    return {
      ...parsedItem,
      items: item.items?.map(parseItem(customPrefixes)),
    };
  };
}

/*
  Recursively adds `to` and `target` attributes to links based on their url
  and resource type.
  It optionally overwrites url paths based on item.type
*/
export function parseMenu(menu: Menu, customPrefixes = {}): EnhancedMenu {
  if (!menu?.items) {
    // eslint-disable-next-line no-console
    console.warn('Invalid menu passed to parseMenu');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return menu;
  }

  return {
    ...menu,
    items: menu.items.map(parseItem(customPrefixes)),
  };
}

export const INPUT_STYLE_CLASSES =
  'appearance-none rounded dark:bg-transparent border focus:border-primary/50 focus:ring-0 w-full py-2 px-3 text-primary/90 placeholder:text-primary/50 leading-tight focus:shadow-outline';

export const getInputStyleClasses = (isError?: string | null) => {
  return `${INPUT_STYLE_CLASSES} ${
    isError ? 'border-red-500' : 'border-primary/20'
  }`;
};

export function statusMessage(status: string) {
  const translations: Record<string, string> = {
    ATTEMPTED_DELIVERY: 'Attempted delivery',
    CANCELED: 'Canceled',
    CONFIRMED: 'Confirmed',
    DELIVERED: 'Delivered',
    FAILURE: 'Failure',
    FULFILLED: 'Fulfilled',
    IN_PROGRESS: 'In Progress',
    IN_TRANSIT: 'In transit',
    LABEL_PRINTED: 'Label printed',
    LABEL_PURCHASED: 'Label purchased',
    LABEL_VOIDED: 'Label voided',
    MARKED_AS_FULFILLED: 'Marked as fulfilled',
    NOT_DELIVERED: 'Not delivered',
    ON_HOLD: 'On Hold',
    OPEN: 'Open',
    OUT_FOR_DELIVERY: 'Out for delivery',
    PARTIALLY_FULFILLED: 'Partially Fulfilled',
    PENDING_FULFILLMENT: 'Pending',
    PICKED_UP: 'Displayed as Picked up',
    READY_FOR_PICKUP: 'Ready for pickup',
    RESTOCKED: 'Restocked',
    SCHEDULED: 'Scheduled',
    SUBMITTED: 'Submitted',
    UNFULFILLED: 'Unfulfilled',
  };
  try {
    return translations?.[status];
  } catch {
    return status;
  }
}

/**
 * Errors can exist in an errors object, or nested in a data field.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function assertApiErrors(data: Record<string, any> | null | undefined) {
  const errorMessage = data?.customerUserErrors?.[0]?.message;
  if (errorMessage) {
    throw new Error(errorMessage);
  }
}

export const DEFAULT_LOCALE: I18nLocale = Object.freeze({
  ...countries.default,
  pathPrefix: '',
});

function findLocaleByCountryCode(code: string): Locale | null {
  const upper = code.toUpperCase();
  return Object.values(countries).find((c) => c.country === upper) ?? null;
}

export function buildLocale(countryCode: string, region: Region): I18nLocale {
  const upper = countryCode.toUpperCase();
  const existing = findLocaleByCountryCode(upper);

  if (existing) {
    return { ...existing, pathPrefix: '' };
  }

  return {
    language: 'EN',
    country: upper as CountryCode,
    label: upper,
    currency: (REGION_FALLBACK_CURRENCY[region] ?? 'GBP') as CurrencyCode,
    pathPrefix: '',
  };
}

/**
 * @inContext always gets the region's representative country (DE / GB / US), never
 * the shopper's own: the country picker chooses a region, not a market.
 */
export function getLocaleFromRequest(request: Request): I18nLocale {
  const cookies = parseCookie(request.headers.get('Cookie') || '');
  const cookieRegion = cookies[COOKIE_NAMES.region];
  const ipCountry =
    request.headers.get('CF-IPCountry') ||
    request.headers.get('x-vercel-ip-country');

  // ponytail: the cookie-less first visit resolves by the hardcoded country sets,
  // while the root loader uses Shopify markets. They agree while the `europe`
  // market is exactly the EU-27; a country added to it outside those sets gets
  // UK pricing until the cookie lands. Fix by resolving markets here too.
  const region = isValidRegion(cookieRegion)
    ? cookieRegion
    : resolveRegionInfo(ipCountry).region;

  return buildLocale(REGION_DEFAULT_COUNTRY_CODES[region], region);
}

export function usePrefixPathWithLocale(path: string) {
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale ?? DEFAULT_LOCALE;

  return `${selectedLocale.pathPrefix}${
    path.startsWith('/') ? path : '/' + path
  }`;
}

export function useIsHomePath() {
  const { pathname } = useLocation();
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale ?? DEFAULT_LOCALE;
  const strippedPathname = pathname.replace(selectedLocale.pathPrefix, '');
  return strippedPathname === '/';
}

/**
 * Validates that a url is local
 * @param url
 * @returns `true` if local `false`if external domain
 */
export function isLocalPath(url: string) {
  try {
    // We don't want to redirect cross domain,
    // doing so could create fishing vulnerability
    // If `new URL()` succeeds, it's a fully qualified
    // url which is cross domain. If it fails, it's just
    // a path, which will be the current domain.
    new URL(url);
  } catch {
    return true;
  }

  return false;
}

/**
 * Shopify's 'Online Store' stores cart IDs in a 'cart' cookie.
 * By doing the same, merchants can switch from the Online Store to Hydrogen
 * without customers losing carts.
 */
export function getCartId(request: Request) {
  const cookies = parseCookie(request.headers.get('Cookie') || '');
  return cookies.cart ? `gid://shopify/Cart/${cookies.cart}` : undefined;
}
